import { DataGridErrorOverlay } from "../../common-ui/DataGridErrorOverlay";
import {
  ProgramRoleSelectFields,
  UserNetworkErrors
} from "../../app/constants";
import {
  UserNotifications,
  showToastForBulkResult,
  useUsersByProgramIds
} from "./userUtils";
import { useAppSelector } from "../../app/hooks";
import { selectCurrentUser } from "./usersSlice";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { uniqBy } from "lodash";
import {
  getActiveProgramAdminedProgramIds,
  getProgramMembershipsAsProgramAdmin
} from "../../app/utils";
import { UserTableProps } from "../../app/types";
import {
  Box,
  Button,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  OutlinedInput,
  Select
} from "@mui/material";
import Grid from "@mui/material/Unstable_Grid2";
import {
  GroupAdd,
  PersonAdd,
  ScheduleSendRounded
} from "@mui/icons-material";
import { useEffect, useState, useRef } from "react";
import { useDispatch } from "react-redux";
import { DataGridPremium, gridClasses } from "@mui/x-data-grid-premium";
import { useAllPrograms } from "../Programs/programUtils";
import {
  Program,
  ProgramMembership,
  ProgramRole
} from "../../API";
import {
  setShowToast,
  setToastAutoHideDuration,
  setToastMessage,
  setToastStyle
} from "../../common-ui/toastSlice";
import LoadingButton from "@mui/lab/LoadingButton";
import { useBulkResendUserInvitations } from "../UserInvitations/userInvitationUtils";
import { bulkUpdatePgy } from "./usersAPI";
import {
  setModalBoxStyle,
  setModalContent,
  setModalTitle,
  setShowModal
} from "../../common-ui/modalSlice";
import PgyDeltaInput from "./PgyDeltaInput";
import {
  selectFeatureFlags,
  selectFeatureFlagsError,
  selectFeatureFlagsStatus
} from "../Firebase/firebaseSlice";
import { useNetworkProgramsByPrograms } from "../Networks/networkUtils";
import { ProgramUser } from "../../custom-types";
import { ProgramRoleSelectFieldsNew } from "./SysAdminsUsersTable";

type ProgramAdminsUsersTableProps = UserTableProps;

export const ProgramAdminsUsersTable = ({
  disableVirtualization = false,
  columns,
  handleRowClick
}: ProgramAdminsUsersTableProps) => {
  const { t } = useTranslation();
  const currentUser = useAppSelector(selectCurrentUser);
  const { programs, status: programsStatus } = useAllPrograms();
  const flags = useAppSelector(selectFeatureFlags);
  const flagsStatus = useAppSelector(selectFeatureFlagsStatus);
  const flagsError = useAppSelector(selectFeatureFlagsError);
  const { allow_bulk_invite } = flags;

  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [pgyModalInvocations, setPgyModalInvocations] = useState(0);
  const [pgyBumpValue, setPgyBumpValue] = useState<number | undefined>(1);

  const [selectedProgramId, setSelectedProgramId] = useState<string>("");
  const [selectedRole, setSelectedRole] = useState<ProgramRole | string>("");
  const [selectedStatus, setSelectedStatus] = useState<string>("");
  const [refetchUsers, setRefetchUsers] = useState<number>(0);
  const [allUsersAdminedPrograms, setAllUsersAdminedPrograms] = useState<
    Program[]
  >([]);

  const [selectedUserIds, setSelectedUserIds] = useState<string[]>([]);

  // Refs to track previous values and prevent unnecessary API calls
  const prevProgramsRef = useRef<Program[]>([]);
  const prevSelectedProgramIdRef = useRef<string>("");
  const prevSelectedRoleRef = useRef<ProgramRole | string>("");
  const prevRefetchUsersRef = useRef<number>(0);
  const prevCurrentUserIdRef = useRef<string | undefined>(undefined);

  const {
    status: resendInvitationsStatus,
    successCount,
    currentStep,
    bulkResendUserInvitations
  } = useBulkResendUserInvitations();

  const {
    networkPrograms,
    status: networkProgramsStatus,
    error: networkProgramsError,
    getNetworkProgramsByPrograms
  } = useNetworkProgramsByPrograms();

  // Grab the initial list of users that have memberships to these programs.
  const {
    users,
    status: getUsersStatus,
    error,
    getUsersByProgramIds
  } = useUsersByProgramIds();

  type ProgramUserWithRawRoles = ProgramUser & { rawRoles: ProgramRole[] };

  const enrichUser = (user: ProgramUser): ProgramUserWithRawRoles => {
    const programMemberships = user.programMemberships?.items || [];
    const activeMemberships = programMemberships.filter((m) => !m?.endDate);

    const rawRoles = activeMemberships
      .map((membership) => membership?.programRole)
      .filter((r): r is ProgramRole => !!r); // Type narrowing

    const roleSet = new Set<string>();
    let hasClinician = false;
    rawRoles.forEach((role) => {
      if (role === "Trainee" || role === "Attending") {
        hasClinician = true;
      } else {
        roleSet.add(role);
      }
    });
    if (hasClinician) roleSet.add("Clinician");

    const trainingPrograms = programs
      .filter((p) =>
        activeMemberships.some(
          (m) => m?.programId === p.id && m.programRole === "Trainee"
        )
      )
      .map((p) => p.name)
      .join(", ");

    const institutionName = programs
      .filter((p) => activeMemberships.some((m) => m?.programId === p.id))
      .map((p) => p.institution?.name)
      .join(", ");

    const programName =
      programs.find((p) => activeMemberships.some((m) => m?.programId === p.id))
        ?.name ?? "N/A";

    return {
      ...user,
      role: Array.from(roleSet).join(", "),
      rawRoles: programMemberships
        .filter(
          (m): m is ProgramMembership & { programRole: ProgramRole } =>
            !!m?.programRole && !m.endDate
        )
        .map((m) => m.programRole),
      trainingProgram: trainingPrograms,
      institutionName,
      program: programName,
      jobTitle: user.jobTitle ?? undefined,
      status: user.eulaAcceptedDate ? "Active" : "Unregistered"
    } as ProgramUserWithRawRoles;
  };

  const usersUnique: ProgramUserWithRawRoles[] = uniqBy(users, "email")
    .map(enrichUser)
    .filter((user) =>
      selectedStatus === "" ? true : user.status === selectedStatus
    );

  const necessaryStatuses = [programsStatus, networkProgramsStatus];

  const anyApiIsLoading = [
    getUsersStatus,
    programsStatus,
    resendInvitationsStatus
  ].includes("loading");

  useEffect(() => {
    if (resendInvitationsStatus === "idle") return;

    if (resendInvitationsStatus === "loading") {
      dispatch(setToastStyle("info"));
      dispatch(
        setToastMessage(
          t(
            `Resending invitation ${currentStep} of ${selectedUserIds.length}...`
          )
        )
      );
      dispatch(setToastAutoHideDuration(null));
      dispatch(setShowToast(true));
    }

    if (resendInvitationsStatus === "succeeded") {
      const style =
        successCount === selectedUserIds.length ? "success" : "warning";
      let message = `Invitation has been re-sent successfully to ${successCount} ${
        successCount === 1 ? "person" : "people"
      }`;
      if (successCount < selectedUserIds.length) {
        message += ` out of ${selectedUserIds.length} selected. Please contact your administrator for further assistance.`;
      }
      dispatch(setToastStyle(style));
      dispatch(setToastMessage(t(message)));
      dispatch(setToastAutoHideDuration(8000));
    }

    if (resendInvitationsStatus === "failed") {
      dispatch(setToastStyle("error"));
      dispatch(
        setToastMessage(
          t(
            "An error ocurred re-sending the invitations. Please contact your administrator for further assistance."
          )
        )
      );
    }
    dispatch(setShowToast(true));
  }, [resendInvitationsStatus, successCount, currentStep]);

  useEffect(() => {
    // Check if programs actually changed
    const programsChanged = 
      !prevProgramsRef.current || 
      prevProgramsRef.current.length !== programs?.length ||
      prevProgramsRef.current.map(p => p.id).sort().join(",") !== programs?.map(p => p.id).sort().join(",");
    
    // Check if other dependencies actually changed
    const selectedProgramIdChanged = prevSelectedProgramIdRef.current !== selectedProgramId;
    const selectedRoleChanged = prevSelectedRoleRef.current !== selectedRole;
    const refetchUsersChanged = prevRefetchUsersRef.current !== refetchUsers;
    const currentUserChanged = prevCurrentUserIdRef.current !== currentUser?.id;

    // Only proceed if something actually changed
    if (!programsChanged && !selectedProgramIdChanged && !selectedRoleChanged && !refetchUsersChanged && !currentUserChanged) {
      return;
    }

    // Update refs
    prevProgramsRef.current = programs || [];
    prevSelectedProgramIdRef.current = selectedProgramId;
    prevSelectedRoleRef.current = selectedRole;
    prevRefetchUsersRef.current = refetchUsers;
    prevCurrentUserIdRef.current = currentUser?.id;

    if (programs && programs.length > 0) {
      const { programMembershipsAsProgramAdmin } =
        getProgramMembershipsAsProgramAdmin(currentUser);  
      const idsOfProgramsUserAdmins = programMembershipsAsProgramAdmin?.map(
        (programMembership) => programMembership?.programId
      );
      const allUsersAdminedPrograms = programs?.filter((program) =>
        idsOfProgramsUserAdmins?.some(
          (adminedProgramId) => adminedProgramId === program.id
        )
      );
      setAllUsersAdminedPrograms(allUsersAdminedPrograms);
      const idsOfAllUsersAdminedPrograms = allUsersAdminedPrograms?.map(
        (program) => program.id
      );
      if (idsOfAllUsersAdminedPrograms.length === 1 && !selectedProgramId) {
        setSelectedProgramId(idsOfAllUsersAdminedPrograms[0]);
      }
      let programsToFetch = idsOfAllUsersAdminedPrograms;
      let roleToFetch = undefined;
      if (selectedProgramId) {
        programsToFetch = [selectedProgramId];
      }
      if (selectedRole !== "") {
        roleToFetch = selectedRole as ProgramRole;
      }
      if (programsToFetch && programsToFetch.length > 0) {
        getUsersByProgramIds(programsToFetch, roleToFetch, true);
      }
    }
    if (currentUser) {
      const activeProgramAdminedProgramIds =
        getActiveProgramAdminedProgramIds(currentUser);
      if (
        activeProgramAdminedProgramIds &&
        activeProgramAdminedProgramIds.length > 0
      ) {
        getNetworkProgramsByPrograms(activeProgramAdminedProgramIds);
      }
    }
  }, [selectedProgramId, selectedRole, refetchUsers, currentUser, programs]);

  useEffect(() => {
    const selectedUsers = selectedUserIds
      .map((userId) =>
        usersUnique.find((user) => user.id === userId && !!user.pgy)
      )
      .filter(Boolean) as typeof usersUnique;

    dispatch(
      setModalContent(
        <Box
          sx={{ display: "flex", flexDirection: "column" }}
          alignItems="center"
          gap={3}
          padding={3}
        >
          <Box>
            You are updating the PGY by{" "}
            <PgyDeltaInput
              setDeltaValue={setPgyBumpValue}
              currentDeltaValue={pgyBumpValue}
            />{" "}
            for {selectedUsers.length} people
          </Box>
          <Button
            variant="contained"
            size="small"
            disabled={!pgyBumpValue}
            onClick={() => {
              if (!pgyBumpValue) return;

              const mutationStatements = selectedUsers
                .map((user) => ({
                  id: user.id,
                  pgy: (user.pgy || 0) + pgyBumpValue
                }))
                .filter(
                  (statement) => statement.pgy >= 1 && statement.pgy <= 12
                );

              if (mutationStatements.length === 0) {
                dispatch(setToastStyle("error"));
                dispatch(
                  setToastMessage(
                    t(
                      "This PGY change can not be performed on any of the selected users, the final PGYs might be out of range"
                    )
                  )
                );
                dispatch(setShowToast(true));
                return;
              }

              bulkUpdatePgy(mutationStatements).then((bulkResult) => {
                showToastForBulkResult(
                  dispatch,
                  bulkResult,
                  selectedUsers.length,
                  t(
                    `You have successfully updated the PGY for ${
                      selectedUsers.length
                    } ${selectedUsers.length > 1 ? "users" : "user"}`
                  ),
                  t(
                    `PGY has been successfully updated for ${bulkResult.fulfilled} out of ${selectedUsers.length} users`
                  ),
                  t(
                    "An error ocurred updating the PGYs, please try again later"
                  )
                );
              });
              setRefetchUsers(refetchUsers + 1);
              dispatch(setShowModal(false));
            }}
          >
            CONFIRM
          </Button>
        </Box>
      )
    );
    dispatch(setModalBoxStyle({ width: 444, minWidth: 444 }));
    if (pgyModalInvocations > 0) dispatch(setShowModal(true));
  }, [pgyModalInvocations, pgyBumpValue]);

  if (necessaryStatuses.includes("loading")) {
    return <CircularProgress />;
  }

  if (necessaryStatuses.every((status) => status === "succeeded")) {
    return (
      <Grid
        sx={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column" }}
        spacing={2}
        gap={2}
      >
        <Grid xs={12} pt={2}>
          <Grid xs={12} container spacing={2}>
            <Grid>
              <FormControl>
                <InputLabel shrink id="program-label">
                  {t("Program")}
                </InputLabel>
                <Select
                  labelId="program-label"
                  label={t("Programs")}
                  id="programSelection"
                  style={{ width: 300 }}
                  size="small"
                  displayEmpty
                  value={selectedProgramId || ""}
                  input={<OutlinedInput notched label={t("Programs")} />}
                  onChange={(event) => setSelectedProgramId(event.target.value)}
                >
                  <MenuItem value={""} key={"no-program"}>
                    All
                  </MenuItem>
                  {allUsersAdminedPrograms?.map((program) => (
                    <MenuItem value={program.id} key={program.id}>
                      {/* TODO - Make a way to share this (and other elements) between the ProgramAdmin and SysAdmin Users tables. I think there's a ticket for that somewhere... */}
                      {`${program.institution?.name} (Institution) > ${program.name} (Program)`}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid>
              <FormControl>
                <InputLabel shrink id="role-label">
                  {t("Role")}
                </InputLabel>
                <Select
                  labelId="role-label"
                  id="roleSelection"
                  label={t("Role")}
                  style={{ width: 300 }}
                  size="small"
                  displayEmpty
                  value={selectedRole || ""}
                  input={<OutlinedInput notched label={t("Role")} />}
                  onChange={(event) => {
                    const value = event.target.value;
                    if (value === "") {
                      setSelectedRole(value);
                    } else {
                      setSelectedRole(value as ProgramRole);
                    }
                  }}
                >
                  <MenuItem value="">All</MenuItem>
                  {ProgramRoleSelectFieldsNew?.map((roleSelectField) => (
                    <MenuItem
                      value={roleSelectField.value}
                      key={roleSelectField.value}
                    >
                      {roleSelectField.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid>
              <FormControl>
                <InputLabel shrink id="status-label">
                  {t("Status")}
                </InputLabel>
                <Select
                  labelId="status-label"
                  id="statusSelection"
                  label={t("Status")}
                  style={{ width: 300 }}
                  size="small"
                  displayEmpty
                  value={selectedStatus || ""}
                  input={<OutlinedInput notched label={t("Status")} />}
                  onChange={(event) => {
                    const value = event.target.value;
                    setSelectedStatus(value);
                  }}
                >
                  <MenuItem value="">All</MenuItem>
                  {[
                    { value: "Active", label: "Active" },
                    { value: "Unregistered", label: "Unregistered" }
                  ]?.map((roleSelectField) => (
                    <MenuItem
                      value={roleSelectField.value}
                      key={roleSelectField.value}
                    >
                      {roleSelectField.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </Grid>

        <Grid xs={12} container spacing={2}>
          <Grid>
            <Button
              variant="contained"
              disabled={anyApiIsLoading}
              size="small"
              startIcon={<PersonAdd />}
              onClick={() => navigate("/invitations/new")}
            >
              {t("NEW USER")}
            </Button>
          </Grid>
          {allow_bulk_invite && (
            <Grid>
              <Button
                variant="contained"
                disabled={anyApiIsLoading}
                size="small"
                startIcon={<GroupAdd />}
                onClick={() => navigate("/invitations/bulkupload")}
              >
                {t("BULK NEW USERS")}
              </Button>
            </Grid>
          )}
          <Grid>
            <LoadingButton
              disabled={selectedUserIds.length === 0 || anyApiIsLoading}
              variant="contained"
              size="small"
              startIcon={<ScheduleSendRounded />}
              loading={resendInvitationsStatus === "loading"}
              loadingPosition="start"
              onClick={() => {
                bulkResendUserInvitations(selectedUserIds);
              }}
            >
              {t("RESEND INVITATION")}
            </LoadingButton>
          </Grid>

          <Grid>
            <Button
              disabled={selectedUserIds.length === 0 || anyApiIsLoading}
              variant="contained"
              size="small"
              onClick={() => {
                const selectedUsers = selectedUserIds
                  .map((userId) =>
                    usersUnique.find((user) => user.id === userId && !!user.pgy)
                  )
                  .filter(Boolean) as typeof usersUnique;

                if (!selectedUsers?.length) {
                  dispatch(
                    setToastMessage(t("There are no applicable users selected"))
                  );
                  dispatch(setToastStyle("error"));
                  dispatch(setShowToast(true));
                  return;
                }

                setPgyModalInvocations(pgyModalInvocations + 1);
              }}
            >
              {t("+1 PGY")}
            </Button>
          </Grid>

          {/* Create a modal to show that report generation has been initiated w/ email message */}
        </Grid>

        {(getUsersStatus === "failed" || error) && (
          <DataGridErrorOverlay
            errorMessage={UserNetworkErrors.FetchAllUsersError}
          />
        )}
        <Grid xs={12} sx={{ flex: 1, minHeight: 0, display: "flex" }}>
          <DataGridPremium
            sx={{
              flex: 1,
              minHeight: 0,
              [`& .${gridClasses.cell}`]: {
                py: 1,
                whiteSpace: "pre-wrap"
              }
            }}
            checkboxSelection
            onRowSelectionModelChange={(selection) => {
              setSelectedUserIds(selection as string[]);
            }}
            disableVirtualization={disableVirtualization}
            aria-label="users-table-programadmins"
            rows={usersUnique || []}
            columns={columns}
            loading={anyApiIsLoading}
            localeText={{
              noRowsLabel: t(UserNotifications.NoUsers)
            }}
            getRowHeight={() => "auto"}
            onRowClick={handleRowClick}
          />
        </Grid>
      </Grid>
    );
  }

  return null;
};
